import React from "react";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  ThemeProvider,
  CssBaseline,
} from "@mui/material";
import { useParams } from "react-router-dom";
import ClientMealPlanContent from "./components/ClientMealPlanContent";
import { createClientTheme } from "./theme/ClientTheme";
import { usePublicMealPlan } from "../../hooks/usePublicMealPlan";

/**
 * ClientMealPlanViewer - Public-facing meal plan viewer for clients
 * This component loads a meal plan by ID and displays it in a client-friendly format
 * No authentication required - designed for sharing meal plans publicly
 */
const ClientMealPlanViewer = () => {
  const { organizationId, mealPlanId } = useParams();

  // Use the new API hook to fetch public meal plan data
  const {
    data: mealPlanData,
    isLoading,
    error: apiError,
  } = usePublicMealPlan(organizationId, mealPlanId);

  // Extract data from API response
  const mealPlan = mealPlanData?.mealPlan;
  const scheduledRecipes = mealPlanData?.scheduledRecipes || [];
  const weeklySchedule = mealPlanData?.weeklySchedule || {};
  const ingredients = mealPlanData?.ingredients || [];
  const dailyNutritionTotals = mealPlanData?.dailyNutritionTotals || {};
  const overallAverages = mealPlanData?.overallAverages || {};
  const organizationBranding = mealPlanData?.organizationBranding || null;

  // Check if we're still loading
  const loading = isLoading || !organizationId || !mealPlanId;

  // Format error message
  const error = apiError ? apiError.message : null;

  // Create custom theme with organization branding from API
  const clientTheme = createClientTheme(organizationBranding);

  // Show loading state if hooks are still loading or data is not available
  if (loading) {
    return (
      <ThemeProvider theme={clientTheme}>
        <CssBaseline />
        <Container>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100vh"
            flexDirection="column"
          >
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading meal plan...
            </Typography>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (error) {
    // Determine specific error message based on error content
    let errorTitle = "Meal Plan Unavailable";
    let errorMessage = error;
    let helpText = [
      "Check that the link is correct",
      "Ensure the meal plan is set to public",
      "Contact support if the problem persists",
    ];

    // Check if this is a 403 forbidden error (not public)
    if (error.includes("not publicly accessible")) {
      errorTitle = "Meal Plan Not Public";
      errorMessage =
        "This meal plan is not publicly accessible. Only public meal plans can be viewed through this link.";
    } else if (error.includes("not found")) {
      errorTitle = "Meal Plan Not Found";
      errorMessage =
        "This meal plan was not found. It may have been deleted or the link is incorrect.";
      helpText = [
        "Check that the link is correct",
        "Verify the meal plan hasn't been deleted",
        "Contact the organization if you believe this is an error",
      ];
    } else if (error.includes("Organization not found")) {
      errorTitle = "Organization Not Found";
      errorMessage =
        "The organization associated with this meal plan could not be found.";
      helpText = [
        "Check that the link is correct",
        "Verify the organization is still active",
        "Contact support if the problem persists",
      ];
    }

    return (
      <ThemeProvider theme={clientTheme}>
        <CssBaseline />
        <Container>
          <Box sx={{ py: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {errorTitle}
              </Typography>
              <Typography variant="body2">{errorMessage}</Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  If you believe this is an error, please:
                </Typography>
                <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                  {helpText.map((text, index) => (
                    <Typography
                      key={index}
                      component="li"
                      variant="body2"
                      sx={{ mb: 0.5 }}
                    >
                      {text}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Alert>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={clientTheme}>
      <CssBaseline />
      <ClientMealPlanContent
        mealPlan={mealPlan}
        recipes={scheduledRecipes.map((sr) => sr.recipe).filter(Boolean)}
        organizationBranding={organizationBranding}
        weeklySchedule={weeklySchedule}
        ingredients={ingredients}
        dailyNutritionTotals={dailyNutritionTotals}
        overallAverages={overallAverages}
        organizationId={organizationId}
        mealPlanId={mealPlanId}
      />
    </ThemeProvider>
  );
};

export default ClientMealPlanViewer;
